import {getCurrentUTCDateTime} from 'src/utils/modals/CurrentDateTimeUTC'

/** Date keys normalized on platform settings save (see platformActions). */
export const SYNC_START_DATE_FIELD_KEYS = ['sync_start_date', 'start_date'] as const

export type SyncStartDateFieldKey = (typeof SYNC_START_DATE_FIELD_KEYS)[number]

export type SyncStartDateGroupConfig = {
  /** platform_settings container key */
  containerKey: string
  /** Parent import toggle on the same container */
  toggleField: string
}

/**
 * Per-group sync start date rules (one row per settings container).
 * Each group reads/writes only its own container on GET/POST.
 */
export const SYNC_START_DATE_REGISTRY: SyncStartDateGroupConfig[] = [
  {containerKey: 'order_setting', toggleField: 'isImportOrderToSource'},
  {containerKey: 'accounting_order_setting', toggleField: 'isImportOrderToSource'},
  {containerKey: 'erp_order_setting', toggleField: 'isImportOrderToSource'},
  {containerKey: 'oms_order_setting', toggleField: 'isImportOrderToSource'},
  {containerKey: 'shipment_setting', toggleField: 'isImportShipmentFromMain'},
  {
    containerKey: 'back_shipment_setting',
    toggleField: 'isImportShipmentFromSecondaryPlatform',
  },
  {containerKey: 'ticket_setting', toggleField: 'isImportTicketsToSource'},
  {containerKey: 'product_setting', toggleField: 'isImportProductFromSource'},
  {containerKey: 'inventory_setting', toggleField: 'isImportInventory'},
  {containerKey: 'erp_inventory_setting', toggleField: 'isImportInventory'},
  {containerKey: 'pricing_setting', toggleField: 'isImportPrice'},
  {containerKey: 'order_payout_setting', toggleField: 'isImportOrderPayout'},
]

type SettingContainer = Record<string, unknown>

/** Mixed platform settings payload (containers, identifiers, primitives, etc.). */
export type PlatformSettingsRecord = Record<string, unknown> | null | undefined

/** Formik / typed settings objects accepted at save boundaries (no index signature required). */
export type PlatformSettingsLike = object | null | undefined

const asPlatformSettingsRecord = (settings: PlatformSettingsLike): PlatformSettingsRecord => {
  if (settings === null || settings === undefined) return settings
  if (typeof settings !== 'object') return null
  return settings as Record<string, unknown>
}

export const isValidSyncStartDate = (value: unknown): value is string => {
  if (value === null || value === undefined) return false
  if (typeof value !== 'string') return false
  const trimmed = value.trim()
  if (trimmed === '') return false
  return Number.isFinite(Date.parse(trimmed))
}

const getSettingContainer = (
  settings: PlatformSettingsRecord,
  containerKey: string
): SettingContainer | null => {
  const container = settings?.[containerKey]
  if (!container || typeof container !== 'object' || Array.isArray(container)) {
    return null
  }
  return container as SettingContainer
}

const omitSyncStartDateFields = (container: SettingContainer): void => {
  SYNC_START_DATE_FIELD_KEYS.forEach((dateKey) => {
    delete container[dateKey]
  })
}

/**
 * Reads a valid saved date for one field from backend GET settings (raw string, unchanged).
 */
export const getSavedSyncStartDateForContainer = (
  savedPlatformSettings: PlatformSettingsRecord,
  containerKey: string,
  dateKey: SyncStartDateFieldKey = 'sync_start_date'
): string | null => {
  const savedContainer = getSettingContainer(savedPlatformSettings, containerKey)
  if (!savedContainer) return null
  const raw = savedContainer[dateKey]
  if (!isValidSyncStartDate(raw)) return null
  return raw.trim()
}

export type ApplySyncStartDatesOnSaveOptions = {
  formValues: PlatformSettingsLike
  /** Authoritative GET response: `retrievedPlatformData.platform_settings` */
  savedPlatformSettings: PlatformSettingsLike
  getCurrentDateTime?: () => string
}

/**
 * Per-group sync start date on save:
 * - Toggle OFF → omit date keys from that container (not null)
 * - Toggle ON → valid saved date from GET for that container/field, else current UTC
 * - Never writes null, empty, or invalid dates
 */
export const applySyncStartDatesOnSave = ({
  formValues,
  savedPlatformSettings,
  getCurrentDateTime = getCurrentUTCDateTime,
}: ApplySyncStartDatesOnSaveOptions): void => {
  const formRecord = asPlatformSettingsRecord(formValues)
  if (!formRecord) return

  const savedRecord = asPlatformSettingsRecord(savedPlatformSettings)

  SYNC_START_DATE_REGISTRY.forEach(({containerKey, toggleField}) => {
    const container = getSettingContainer(formRecord, containerKey)
    if (!container) return

    const toggleOn = container[toggleField] === true

    if (!toggleOn) {
      omitSyncStartDateFields(container)
      return
    }

    SYNC_START_DATE_FIELD_KEYS.forEach((dateKey) => {
      const savedDate = getSavedSyncStartDateForContainer(savedRecord, containerKey, dateKey)
      if (savedDate !== null) {
        container[dateKey] = savedDate
        return
      }

      if (dateKey === 'sync_start_date') {
        container[dateKey] = getCurrentDateTime()
        return
      }

      delete container[dateKey]
    })
  })
}
